import { Tooltip } from '@material-ui/core';
import { PautaEvento, StatusResultado } from 'api/Assessor/pautas';
import {
  BarChartIcon,
  CircleTimes,
  SyncIcon,
  UserIcon,
} from 'components/Icons';
import ToolsIcon from 'components/Icons/ToolsIcon';
import { AssemblyModalitiesNumber } from 'helpers/enums/Assembleia';
import { AssembleiaPautaStatus } from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { VerificarInconsistenciaAssessorRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import { Button, ButtonThemes } from 'plataforma-fundacao-componentes';
import { Fragment, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IAssembleiasAssessorEvent } from 'store/modules/AssembleiasAssessorReducer';
import {
  assessorActionLiberaResultadoPauta,
  assessorActionLiberarResultadoAssociado,
} from 'store/modules/assessor/actions';
import { IReducer } from 'store/Reducer';
import useStyle from '../style';

interface Props {
  pauta: PautaEvento;
  evento: IAssembleiasAssessorEvent;
}

export default function LiberacaoResultadoActions({ pauta, evento }: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { liberandoResultadoPauta } = useSelector(
    (state: IReducer) => state.assessor
  );

  const history = useHistory();

  const canShowLiberacaoResultado = useMemo(() => {
    if (!evento) {
      return false;
    }
    if (!pauta.itens.length) {
      return false;
    }

    if (
      evento.modo >= AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA &&
      pauta.status === AssembleiaPautaStatus.Encerrada
    ) {
      return true;
    }

    if (pauta.status === AssembleiaPautaStatus.Encerrada) {
      return true;
    }

    if (evento.status === AssembleiaStatus.Encerrado) {
      return true;
    }
    return false;
  }, [evento, pauta]);

  const toggleResultado = useCallback(
    async (status: StatusResultado) => {
      dispatch(
        assessorActionLiberaResultadoPauta(
          evento.id,
          pauta.idPauta,
          status,
          ErrorHandler
        )
      );
    },
    [dispatch, evento.id, pauta.idPauta]
  );

  return (
    <Fragment>
      {!pauta.resultadoLiberado && canShowLiberacaoResultado && (
        <Tooltip
          title={translation.resultadoPresencialNaoliberadoToast}
          placement='top'
          arrow
          disableFocusListener={
            evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
              ? pauta.resultadoLiberadoPresencial
              : true
          }
          disableHoverListener={
            evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
              ? pauta.resultadoLiberadoPresencial
              : true
          }
          disableTouchListener={
            evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
              ? pauta.resultadoLiberadoPresencial
              : true
          }
        >
          <span>
            <Button
              style={
                evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
                  ? !pauta.resultadoLiberadoPresencial
                    ? { pointerEvents: 'none' }
                    : {}
                  : {}
              }
              data-testid={'assessor-pauta-consolidar-resultados'}
              theme={ButtonThemes.Primary}
              className={classes.botaoAcoes}
              leftIcon={
                liberandoResultadoPauta ? (
                  <SyncIcon animated />
                ) : (
                  <BarChartIcon />
                )
              }
              onClick={() => toggleResultado(StatusResultado.Liberar)}
              disabled={
                evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
                  ? !pauta.resultadoLiberadoPresencial
                  : false
              }
            >
              {translation.AssembleiaPauta.acoes.consolidarResultados}
            </Button>
          </span>
        </Tooltip>
      )}
      {canShowLiberacaoResultado &&
      pauta.resultadoLiberado &&
      !pauta.resultadoLiberadoAssociado ? (
        <Button
          data-testid={'assessor-pauta-liberar-resultados-associados'}
          theme={ButtonThemes.PrimaryLight}
          leftIcon={
            liberandoResultadoPauta ? <SyncIcon animated /> : <UserIcon />
          }
          disabled={
            pauta.flgSecreta === true &&
            evento.status === AssembleiaStatus.Live &&
            pauta.status === AssembleiaPautaStatus.Encerrada
          }
          onClick={() => {
            dispatch(
              assessorActionLiberarResultadoAssociado(
                evento.id,
                pauta.idPauta,
                ErrorHandler
              )
            );
          }}
        >
          {translation.AssembleiaPauta.acoes.liberarResultadoAssociados}
        </Button>
      ) : undefined}
      {evento?.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO ? (
        pauta.status === AssembleiaPautaStatus.Encerrada &&
        pauta.resultadoLiberadoPresencial &&
        ['AGO', 'AOE', 'AGE'].includes(evento?.tipo || '') ? (
          <Button
            data-testid={'assessor-pauta-verificar-inconsistencia'}
            theme={ButtonThemes.Secondary}
            className={classes.botaoAcoes}
            leftIcon={<ToolsIcon />}
            onClick={() => {
              history.push(
                `${VerificarInconsistenciaAssessorRoutePath.replace(
                  ':id',
                  String(evento?.id)
                ).replace(':idPauta', String(pauta!?.idPauta))}`
              );
            }}
          >
            {translation.AssembleiaPauta.acoes.verificarInconsistencia}
          </Button>
        ) : undefined
      ) : pauta.status === AssembleiaPautaStatus.Encerrada &&
        ['AGO', 'AOE', 'AGE'].includes(evento?.tipo || '') ? (
        <Button
          data-testid={'assessor-pauta-verificar-inconsistencia'}
          theme={ButtonThemes.Secondary}
          className={classes.botaoAcoes}
          leftIcon={<ToolsIcon />}
          onClick={() => {
            history.push(
              `${VerificarInconsistenciaAssessorRoutePath.replace(
                ':id',
                String(evento?.id)
              ).replace(':idPauta', String(pauta!?.idPauta))}`
            );
          }}
        >
          {translation.AssembleiaPauta.acoes.verificarInconsistencia}
        </Button>
      ) : undefined}

      {canShowLiberacaoResultado && pauta.resultadoLiberadoAssociado && (
        <Button
          data-testid={'assessor-pauta-bloquear-resultados'}
          theme={ButtonThemes.Danger}
          className={classes.botaoDeletar}
          leftIcon={
            liberandoResultadoPauta ? <SyncIcon animated /> : <CircleTimes />
          }
          onClick={() => toggleResultado(StatusResultado.Bloquear)}
        >
          {translation.AssembleiaPauta.acoes.bloquearResultados}
        </Button>
      )}
    </Fragment>
  );
}
